/** @jsxImportSource preact */
import { err, type Result } from "neverthrow";
import type { VNode } from "preact";
import { useCallback, useId, useRef, useState } from "preact/hooks";

import {
  readCartLines,
  readCartProduct,
  type CartLines,
  type CartProductDetail,
  type StorefrontCart,
} from "../../cart/cart";
import {
  addAndConfirm,
  changeAndConfirm,
  readLineQuantity,
  recheckAdd,
  type AddOutcome,
} from "../../cart/purchase";
import { initialSelection, selectedVariant } from "../../cart/selection";
import * as copy from "../../paraglide/messages.js";
import { formVars } from "../form/form-styles";
import type { ResolvedProduct, WidgetCartReceipt } from "../message";
import type { WidgetTheme } from "../theme";
import { cartLineId } from "./cart-receipt";
import { AddSheetView, CartSheetLayer, EditSheetView } from "./cart-sheet";
import {
  addSheetModel,
  clampNote,
  editSheetModel,
  roomOf,
  type AddSheetState,
  type CartFacts,
  type EditSheetState,
} from "./sheet-model";

/** 商品卡和回执的加购入口：读商品能不能买、打开面板。没有店面时整组不存在。 */
export interface CartOpeners {
  /** 去店面读这件商品的选项、变体和能不能买。 */
  readProduct: (url: string) => Promise<Result<CartProductDetail, "failed">>;
  openAdd: (
    product: ResolvedProduct,
    detail: CartProductDetail,
    trigger: HTMLElement,
  ) => void;
  openEdit: (receipt: WidgetCartReceipt, trigger: HTMLElement) => void;
  /** 有写入正在进行：别处的加购和改数量入口先禁用。 */
  busy: boolean;
}

type AddSheet = AddSheetState & { kind: "add" };
type EditSheet = EditSheetState & { kind: "edit" };

/**
 * 等这次重绘落地（回执插进消息列表、面板卸掉）后再挪焦点。改完数量时旧回执被新回执顶替，
 * 触发的按钮已经不在了，就去找同一行购物车那条新回执上的「Edit quantity」。
 */
function focusLater(trigger: HTMLElement | undefined, lineId?: string) {
  if (!trigger) return;
  const root = trigger.getRootNode();
  setTimeout(() => {
    const target =
      trigger.isConnected || lineId === undefined
        ? trigger
        : root instanceof ShadowRoot || root instanceof Document
          ? Array.from(
              root.querySelectorAll<HTMLElement>("[data-cart-edit]"),
            ).find((element) => element.dataset.cartEdit === lineId)
          : undefined;
    if (target?.isConnected) target.focus({ preventScroll: true });
  }, 0);
}

/**
 * 加购和改数量的面板：一次只开一个，一次只写一笔。所有件数以 Shopify 返回为准；
 * 店面接口不给库存数，所以「最多几件」只在 Shopify 少加或拒绝之后才知道，记在这里给后面的选择用。
 * 面板上显示什么由 sheet-model 算，这里只管读写和焦点。
 */
export function useCartSheet({
  storefront,
  onReceipt,
  theme,
  touch,
}: {
  storefront?: StorefrontCart;
  onReceipt?: (receipt: WidgetCartReceipt) => void;
  theme: WidgetTheme;
  touch: boolean;
}): { openers?: CartOpeners; layer: VNode | null } {
  const [sheet, setSheet] = useState<AddSheet | EditSheet>();
  const [lines, setLines] = useState<CartLines>();
  // 变体 id → Shopify 告诉过的这一行最多能放几件。
  const [known, setKnown] = useState<Record<number, number>>({});
  const writing = useRef(false);
  const trigger = useRef<HTMLElement>();
  // 每开一次面板换一个号：关掉之后才回来的读取结果不再落到新面板上。
  const opened = useRef(0);
  const titleId = useId();
  const qtyId = useId();
  // 商品卡按它决定要不要重读：店面不变就一直是同一个函数。
  const rootUrl = storefront?.rootUrl;
  const shopDomain = storefront?.shopDomain;
  const readProduct = useCallback(
    (url: string): Promise<Result<CartProductDetail, "failed">> =>
      rootUrl === undefined || shopDomain === undefined
        ? Promise.resolve(err("failed"))
        : readCartProduct(url, { rootUrl, shopDomain }),
    [rootUrl, shopDomain],
  );

  if (!storefront || !onReceipt) return { layer: null };

  const facts: CartFacts = { lines, known, locale: storefront.locale };
  const busy =
    sheet?.kind === "add"
      ? sheet.phase === "adding" || sheet.phase === "checking"
      : sheet?.kind === "edit" && sheet.phase === "saving";
  const learn = (variantId: number, cap: number) => {
    setKnown((current) => ({ ...current, [variantId]: cap }));
  };

  const openAdd = (
    product: ResolvedProduct,
    detail: CartProductDetail,
    from: HTMLElement,
  ) => {
    if (writing.current) return;
    const token = ++opened.current;
    trigger.current = from;
    setSheet({
      kind: "add",
      product,
      detail,
      selection: initialSelection(detail, product.variantId),
      qtyText: "1",
      phase: "idle",
    });
    // 读一下购物车：已有几件、用什么币种标价。读不到也能加购，只是少这两样信息。
    void readCartLines(storefront).then((result) => {
      if (token === opened.current)
        setLines(result.isOk() ? result.value : undefined);
    });
  };

  const readLine = (receipt: WidgetCartReceipt, token: number) => {
    void readLineQuantity(receipt.variantId, receipt.lineKey, storefront).then(
      (result) => {
        if (token !== opened.current) return;
        setSheet((current) =>
          current?.kind !== "edit"
            ? current
            : {
                ...current,
                line:
                  result.status === "ok"
                    ? {
                        status: "ok",
                        key: result.key,
                        quantity: result.quantity,
                      }
                    : result,
                draft:
                  result.status === "ok"
                    ? String(result.quantity)
                    : current.draft,
              },
        );
      },
    );
  };

  const openEdit = (receipt: WidgetCartReceipt, from: HTMLElement) => {
    if (writing.current) return;
    const token = ++opened.current;
    trigger.current = from;
    setSheet({
      kind: "edit",
      receipt,
      line: { status: "loading" },
      draft: String(receipt.quantity),
      phase: "idle",
    });
    readLine(receipt, token);
  };

  const close = () => {
    if (writing.current) return;
    opened.current += 1;
    setSheet(undefined);
    focusLater(trigger.current);
  };

  const openers: CartOpeners = { readProduct, openAdd, openEdit, busy };
  if (!sheet) return { openers, layer: null };

  const layerStyle = {
    ...formVars(theme),
    "--gn-sheet-bg": theme.surfaceSolid,
  };

  if (sheet.kind === "add") {
    const { product, detail, selection } = sheet;
    const model = addSheetModel(sheet, facts);
    const { variant, quantity, room } = model;
    const update = (patch: Partial<AddSheet>) => {
      setSheet({
        ...sheet,
        phase: "idle",
        message: undefined,
        before: undefined,
        ...patch,
      });
    };
    const settle = () => {
      if (quantity === undefined)
        update({ qtyText: "1", note: copy.widget_cart_lowest() });
      else if (variant && room > 0 && quantity > room)
        update({
          qtyText: String(room),
          note: clampNote(facts, variant.id, room),
        });
    };
    // 加购或重新核对之后：加上了就关面板插回执，否则面板留着、选择不动，状态行说明结果。
    const finish = (outcome: AddOutcome, token: number) => {
      writing.current = false;
      if (token !== opened.current || !variant || !quantity) return;
      if (outcome.lines) setLines(outcome.lines);
      if (outcome.status === "added") {
        if (outcome.added < quantity) learn(variant.id, outcome.quantity);
        opened.current += 1;
        setSheet(undefined);
        onReceipt({
          kind: "added",
          title: product.title,
          variant: model.label || undefined,
          imageUrl: variant.imageUrl ?? product.imageUrl,
          variantId: variant.id,
          lineKey: outcome.key,
          quantity: outcome.quantity,
          added: outcome.added,
          requested: quantity,
        });
        focusLater(trigger.current);
        return;
      }
      if (outcome.status === "rejected" && outcome.inCart !== undefined)
        learn(variant.id, outcome.inCart);
      setSheet({
        ...sheet,
        phase: outcome.status,
        note: undefined,
        message: outcome.status === "rejected" ? outcome.message : undefined,
        before: outcome.status === "unconfirmed" ? outcome.before : undefined,
      });
    };
    const add = () => {
      if (
        writing.current ||
        model.reason !== undefined ||
        !variant ||
        !quantity
      )
        return;
      if (model.recheck) {
        const { before } = sheet;
        if (before === undefined) return;
        writing.current = true;
        const token = opened.current;
        setSheet({ ...sheet, phase: "checking" });
        void recheckAdd(variant.id, before, storefront).then((outcome) => {
          finish(outcome, token);
        });
        return;
      }
      if (quantity > room) {
        settle();
        return;
      }
      writing.current = true;
      const token = opened.current;
      setSheet({
        ...sheet,
        phase: "adding",
        note: undefined,
        message: undefined,
      });
      void addAndConfirm(variant.id, quantity, storefront).then((outcome) => {
        finish(outcome, token);
      });
    };
    const step = (delta: 1 | -1) => {
      const next = Math.min(
        Math.max(1, room),
        Math.max(1, (quantity ?? 1) + delta),
      );
      update({ qtyText: String(next), note: undefined });
    };
    return {
      openers,
      layer: (
        <CartSheetLayer
          labelledBy={titleId}
          locked={busy}
          onDismiss={close}
          style={layerStyle}
          touch={touch}
        >
          <AddSheetView
            cartHref={model.showCart ? `${storefront.rootUrl}cart` : undefined}
            imageUrl={model.imageUrl}
            locked={busy}
            onClose={close}
            onPick={(index, value) => {
              const next = [...selection];
              next[index] = value;
              const picked = selectedVariant(detail, next);
              const pickedRoom = picked ? roomOf(facts, picked.id) : 0;
              const clamp =
                picked?.available &&
                quantity !== undefined &&
                pickedRoom > 0 &&
                quantity > pickedRoom;
              update({
                selection: next,
                ...(clamp
                  ? {
                      qtyText: String(pickedRoom),
                      note: clampNote(facts, picked.id, pickedRoom),
                    }
                  : { note: undefined }),
              });
            }}
            options={model.options}
            price={model.price}
            primary={{ ...model.primary, onClick: add }}
            quantity={{
              id: qtyId,
              hint: model.hint,
              text: sheet.qtyText,
              decreaseDisabled: model.decreaseDisabled,
              increaseDisabled: model.increaseDisabled,
              onStep: step,
              onType: (text) => {
                update({ qtyText: text, note: undefined });
              },
              onSettle: settle,
            }}
            status={model.status}
            title={model.title}
            titleId={titleId}
            tokens={theme}
          />
        </CartSheetLayer>
      ),
    };
  }

  const { receipt, line } = sheet;
  const model = editSheetModel(sheet, facts);
  const { quantity, max } = model;
  const update = (patch: Partial<EditSheet>) => {
    setSheet({ ...sheet, phase: "idle", message: undefined, ...patch });
  };
  const settle = () => {
    if (quantity === undefined)
      update({ draft: "1", note: copy.widget_cart_lowest() });
    else if (quantity > max)
      update({
        draft: String(max),
        note: copy.widget_cart_clamp_available({ count: max, available: max }),
      });
  };
  const save = (to: number) => {
    if (writing.current || line.status !== "ok") return;
    if (to > max) {
      settle();
      return;
    }
    writing.current = true;
    const token = opened.current;
    setSheet({
      ...sheet,
      phase: "saving",
      tried: to,
      draft: String(to),
      note: undefined,
      message: undefined,
    });
    void changeAndConfirm(
      receipt.variantId,
      line.key,
      line.quantity,
      to,
      storefront,
    ).then((outcome) => {
      writing.current = false;
      if (token !== opened.current) return;
      if (outcome.status === "changed") {
        if (outcome.quantity < to) learn(receipt.variantId, outcome.quantity);
        opened.current += 1;
        setSheet(undefined);
        const updated: WidgetCartReceipt = {
          ...receipt,
          kind: "updated",
          lineKey: line.key,
          quantity: outcome.quantity,
          added: undefined,
          requested: to,
        };
        onReceipt(updated);
        focusLater(trigger.current, cartLineId(updated));
        return;
      }
      if (outcome.status === "missing") {
        setSheet({ ...sheet, phase: "idle", line: { status: "missing" } });
        return;
      }
      // 没改成：数量框回到 Shopify 确认的件数，Retry 再按刚才要改成的数重来。
      const confirmed =
        outcome.status === "unconfirmed"
          ? line.quantity
          : (outcome.quantity ?? line.quantity);
      setSheet({
        ...sheet,
        phase: outcome.status,
        tried: to,
        line: { ...line, quantity: confirmed },
        draft: String(confirmed),
        note: undefined,
        message: outcome.status === "rejected" ? outcome.message : undefined,
      });
    });
  };
  return {
    openers,
    layer: (
      <CartSheetLayer
        labelledBy={titleId}
        locked={busy}
        onDismiss={close}
        style={layerStyle}
        touch={touch}
      >
        <EditSheetView
          frozen={model.frozen}
          imageUrl={receipt.imageUrl}
          inCart={model.current}
          locked={busy}
          onClose={close}
          primary={{
            ...model.primary,
            onClick: () => {
              if (model.reread) {
                update({ line: { status: "loading" } });
                readLine(receipt, opened.current);
              } else if (model.retrySave && sheet.tried !== undefined)
                save(sheet.tried);
              else if (quantity !== undefined) save(quantity);
            },
          }}
          quantity={{
            id: qtyId,
            hint: model.hint,
            text: sheet.draft,
            decreaseDisabled: model.decreaseDisabled,
            increaseDisabled: model.increaseDisabled,
            onStep: (delta) => {
              update({
                draft: String(
                  Math.min(max, Math.max(1, (quantity ?? 1) + delta)),
                ),
                note: undefined,
              });
            },
            onType: (text) => {
              update({ draft: text, note: undefined });
            },
            onSettle: settle,
          }}
          status={model.status}
          title={receipt.title}
          titleId={titleId}
          tokens={theme}
          variant={receipt.variant}
        />
      </CartSheetLayer>
    ),
  };
}
